<script setup lang="ts">
import { FdxButton, FdxDropdown, FdxLoadingSpinner } from '@foodics/ui-common';
import UserAvatar from './UserAvatar.vue';

import { useCurrentUser } from '@/composables/currentUser';
import { useLocale } from '@/composables/locale';

import { SUPPORT_LOCALES } from '@/locales/config';
import { useAuth } from '@/composables/auth';
import { useRouter } from 'vue-router';
import { ref, watch } from 'vue';
import NotificationsCenterDropdown from './notifications/NotificationsCenterDropdown.vue';
const emit = defineEmits<{
    openSidebarDrawer: [];
}>();

const router = useRouter();
const { logout, isLoggingOut } = useAuth();
const { user } = useCurrentUser();
const { locale, setLocale } = useLocale();

async function logoutUser() {
    await logout();
    router.push('/login');
}

const localisationDropdownShown = ref(false);
function toggleLocalisationDropdown() {
    localisationDropdownShown.value = !localisationDropdownShown.value;
}

const userDropdownShown = ref(false);
watch(userDropdownShown, (isShown) => {
    // hide the localisation dropdown when the user dropdown is closed
    if (!isShown) localisationDropdownShown.value = false;
});
</script>

<template>
    <nav
        class="bg-white text-white p-2 border-b border-b-gray-200 flex items-center justify-between"
        data-testid="nav-container"
    >
        <FdxIconMenu
            class="text-gray-1000 size-6 opacity-100 lg:opacity-0 lg:pointer-events-none transition-opacity duration-300 cursor-pointer"
            data-testid="nav-button-menu-toggle"
            @click="emit('openSidebarDrawer')"
        />

        <div class="flex items-center gap-2">
            <!-- Notifications -->
            <NotificationsCenterDropdown />

            <!-- User Dropdown -->
            <FdxDropdown
                placement="bottom-end"
                data-testid="nav-dropdown-user"
                :popper-hide-triggers="[]"
                v-model:shown="userDropdownShown"
            >
                <template #trigger>
                    <FdxButton
                        class="flex items-center border-none shadow-none active:bg-gray-100 hover:bg-gray-100 p-0"
                        variant="secondary"
                        :disabled="!user"
                        data-testid="nav-button-user-avatar"
                    >
                        <UserAvatar v-if="user" :user="user!" />
                        <div v-else class="text-gray-1000 size-10">
                            <FdxLoadingSpinner
                                loading-text=""
                                class="w-full h-full"
                            />
                        </div>

                        <FdxIconKeyboardArrowDown
                            class="text-gray-1000 size-6"
                        />
                    </FdxButton>
                </template>
                <template #menu="{ hide: hideUserDropdown }">
                    <ul class="w-48 pb-2">
                        <li class="py-3 px-4">
                            <div v-if="user" class="rtl:text-end">
                                <div
                                    class="text-sm"
                                    data-testid="nav-text-user-name"
                                >
                                    {{ user!.name }}
                                </div>
                                <div
                                    class="text-gray-500 text-xs"
                                    data-testid="nav-text-user-email"
                                >
                                    {{ user!.email }}
                                </div>
                            </div>
                            <div v-else class="rtl:text-end">
                                <!-- shouldn't reach here unless the user hacks the browser's Inspector -->
                                <FdxLoadingSpinner
                                    class="rtl:!justify-end ltr:!justify-start"
                                    :loading-text="
                                        $t('generic.loading.userData')
                                    "
                                />
                            </div>
                        </li>
                        <li>
                            <FdxDropdown
                                class="w-full cursor-default"
                                placement="left-start"
                                :triggers="[]"
                                :shown="localisationDropdownShown"
                            >
                                <template #trigger>
                                    <FdxButton
                                        class="bg-white hover:bg-gray-100 active:bg-gray-100 active:text-black rounded-none shadow-none border-none text-black py-3 px-4 w-full border-y border-gray-100 flex text-sm justify-between"
                                        @mouseenter="
                                            localisationDropdownShown = true
                                        "
                                        @click="toggleLocalisationDropdown"
                                    >
                                        <div class="flex gap-x-2">
                                            <div
                                                class="space-x-0.5 rtl:flex-row-reverse flex items-center capitalize"
                                            >
                                                <span>
                                                    {{
                                                        $t(
                                                            'generic.common_terms.language',
                                                        )
                                                    }}
                                                    :
                                                </span>
                                            </div>
                                            <span>
                                                {{
                                                    $t(
                                                        `generic.symbols.${locale}`,
                                                    )
                                                }}
                                            </span>
                                        </div>
                                        <div>
                                            <FdxIconKeyboardArrowRight
                                                class="size-5 rtl:rotate-180"
                                            />
                                        </div>
                                    </FdxButton>
                                </template>
                                <template #menu>
                                    <div class="w-44 p-1">
                                        <FdxButton
                                            class="py-2.5 px-4 text-sm hover:bg-gray-100 rounded cursor-pointer duration-200 rtl:text-end flex justify-between focus:outline-none focus:bg-gray-100 w-full border-none shadow-none bg-white active:bg-gray-200 text-black active:text-black focus:text-black hover:text-black"
                                            v-for="loc in SUPPORT_LOCALES"
                                            :key="loc"
                                            :data-testid="`nav-button-language-${loc}`"
                                            @click="[
                                                setLocale(loc),
                                                toggleLocalisationDropdown(),
                                                hideUserDropdown(),
                                            ]"
                                        >
                                            <span>
                                                {{
                                                    $t(`generic.symbols.${loc}`)
                                                }}
                                            </span>
                                            <FdxIconCheck
                                                v-if="locale === loc"
                                                class="size-5"
                                            />
                                        </FdxButton>
                                    </div>
                                </template>
                            </FdxDropdown>
                            <FdxButton
                                class="bg-white w-full text-start border-none rounded-none shadow-none py-3 px-4 text-red-600 text-sm hover:bg-gray-100 hover:text-red-600 cursor-pointer active:bg-gray-100 active:text-red-600 duration-200 focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-opacity-50"
                                :class="{
                                    'rtl:flex rtl:justify-end': isLoggingOut,
                                }"
                                :loading="isLoggingOut"
                                data-testid="nav-button-logout"
                                @click="logoutUser"
                                @keydown.enter="logoutUser"
                            >
                                {{ $t('generic.forms.logout') }}
                            </FdxButton>
                        </li>
                    </ul>
                </template>
            </FdxDropdown>
        </div>
    </nav>
</template>
